/**
 * The Xonera mark.
 *
 * Reconstructed from `src/assets/xonera-logo.jpg`, a 462x421 raster. Every
 * value below is measured off that file rather than chosen:
 *
 *   - The field is pure `#000000` (90.7% of its pixels) and the strokes are
 *     `#D9D9D9` (7.1%) — a light grey, NOT white. That is the "two-tone" the
 *     mark is built from.
 *   - The artwork sits well inside the field: its ink box is 260x253 in a
 *     462x421 frame, so it occupies ~58% and carries ~20% padding on every
 *     side. `scale(0.62)` reproduces that ratio in the 64-unit viewBox.
 *
 * Not an auto-trace. The mark is built entirely from round-capped straight
 * strokes, so it reproduces exactly as four 3-point polylines. The geometry was
 * fitted by rasterising candidates against the original and minimising pixel
 * mismatch; the residual is a 1px anti-aliasing fringe. The four strokes form
 * two rotationally-symmetric pairs — path 3 is path 1 rotated 180 degrees about
 * the centre, and 4 is 2 — exact here rather than the ~5px off the JPEG carried
 * from compression. The path centre is exactly (32, 32), which is why the inset
 * scale can be taken about that point.
 *
 * The rounded corner is the one value NOT from the original, which was a hard
 * square. `rx=14` is 21.9% of the tile, the app-icon convention, and lands on
 * 7px at the 32px size the navbar renders.
 *
 * COLOURS ARE FIXED, NOT TOKENS. A logo is brand, so it must not recolour with
 * the theme. This is a real change from the previous version, where the mark
 * was bare strokes in `currentColor` — call sites no longer pass a text colour.
 *
 * KNOWN: a `#000000` tile on the dark theme's `#0F1015` ground measures 1.11:1,
 * so the tile edge is effectively invisible there and the mark reads as grey
 * strokes floating on the page. That is the same weakness the original raster
 * had. Fixing it means either a hairline ring in dark or a non-black field;
 * both are design calls, so neither is taken here.
 */
interface XoneraMarkProps {
  /** Tailwind sizing classes. Colour is fixed by the mark itself. */
  className?: string;
  /**
   * Set when the mark appears WITHOUT the wordmark beside it, so the logo still
   * has an accessible name. Leave unset next to a visible "Xonera" — then the
   * mark is decorative and is hidden from assistive tech.
   */
  title?: string;
}

export function XoneraMark({ className = '', title }: XoneraMarkProps) {
  return (
    <svg
      viewBox='0 0 64 64'
      className={className}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <rect width='64' height='64' rx='14' fill='#000000' />
      <g
        transform='translate(32 32) scale(0.62) translate(-32 -32)'
        fill='none'
        stroke='#D9D9D9'
        strokeWidth={4.36}
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path d='M11.84 4.18L33.19 25.46L42.83 16.34' />
        <path d='M59.57 12.42L39.45 32.63L48.61 42.21' />
        <path d='M21.17 47.66L30.81 38.54L52.16 59.82' />
        <path d='M15.39 21.79L24.55 31.37L4.43 51.58' />
      </g>
    </svg>
  );
}
